import { Inject, Injectable } from '@angular/core';
import { DEFAULT_NAMING_KEY, FieldNames } from '@shared/naming/naming.module';
import { NamingConfig } from '@shared/naming/naming-config';
import { fuzzyKeyLookup } from '@shared/utilities/fuzzyKeyLookup';

export type ModelFieldNames<TModel extends Record<string, any>> = {
  map: Record<keyof TModel, string>;
  get(key: keyof TModel): string | undefined;
};

@Injectable()
export class FieldNameService<Model extends Record<string, any> = any> {
  readonly map!: Record<keyof Model, string>;
  get!: <T = any>(key: T) => T extends keyof Model ? string : undefined;

  constructor(
    @Inject(DEFAULT_NAMING_KEY) public readonly defaultKey: string,
    @Inject(FieldNames)
    public readonly fieldMaps: Record<string, Record<string, string> | undefined>
  ) {
    Object.assign(this, this.for(defaultKey));
  }

  for(model: string | NamingConfig) {
    const key = typeof model === 'string' ? model : model.key;
    return this.from(this.fieldMaps[key] ?? {});
  }

  // Decided against using an actual Map object because it would limit customizability and would need
  // special treatment when passed into shared helper functions like fuzzyKeyLookup.
  from<TModel extends Record<string, any>>(
    map: Record<keyof TModel, string>
  ): ModelFieldNames<TModel> {
    return {
      map,
      get(key: keyof TModel & string): string | undefined {
        return fuzzyKeyLookup(this.map, key);
      },
    };
  }
}
